import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../components/organisms/Layout";
import Input from "../../../components/form/Input";
import Select from "../../../components/form/Select";
import Category from "../../../api/Category";
import Certification from "../../../api/Certification";
import { Loader2 } from "lucide-react";

const AddCertification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const editMode = params?.id;

  const [name, setName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [validationErrors, setValidationErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = editMode
      ? "HeyHire Portal-Edit Certification"
      : "HeyHire Portal-Add Certification";
    loadCategories();
    if (editMode) {
      loadCertification();
    }
  }, [editMode]);

  const loadCategories = () => {
    Category.getAll().then((response) => {
      setCategories(response.data.data);
      if (!editMode && response.data.data.length > 0) {
        setSelectedCategory(response.data.data[0].id);
      }
    });
  };

  const loadCertification = () => {
    setLoading(true);
    Certification.getById(params.id)
      .then((response) => {
        const cert = response.data.data;
        setName(cert.name);
        setSelectedCategory(cert.category[0]?.id);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setValidationErrors(null);

    const data = {
      name,
      category_id: selectedCategory,
    };

    const apiCall = editMode
      ? Certification.update(params.id, data)
      : Certification.add(data);

    apiCall
      .then(() => {
        toast.success(
          editMode
            ? t("certifications.certification-updated")
            : t("certifications.certification-added")
        );
        navigate("/admin/certifications");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong"));
          }
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <DashboardLayout >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <Link
              to="/admin/certifications"
              className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <i className="far fa-chevron-left text-xl" />
            </Link>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editMode
                  ? t("certifications.edit-certification")
                  : "Add Certification"}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {editMode
                  ? "Update the certification details below."
                  : "Fill in the information below to create a new certification."}
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                Certification Information
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the certification.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    name="name"
                    label="Certification name"
                    type="text"
                    value={name}
                    placeholder="Enter certification name"
                    error={validationErrors?.name?.[0]}
                    onChange={(e) => setName(e.target.value)}
                    
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <Select
                    label={t("common.category")}
                    value={selectedCategory}
                    name="category"
                    error={validationErrors?.category_id?.[0]}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10">
              <button
                type="button"
                onClick={() => navigate("/admin/certifications")}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : editMode ? (
                  t("common.save")
                ) : (
                  t("common.add")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddCertification;
